import {
  type ApiResponse,
  HTTPMethod,
  request,
} from '../../../../global/api/legacyApi'

export type PollDto = {
  /** ID of the Poll */
  poll_id: number
  /** ID of the object of which the poll belongs */
  object_id: number
  /** Type of the object of which the poll belongs */
  object_type: 'live' // only live supported atm.
  /** The poll question */
  question: string
  /* Date string of when the poll was opened. */
  opened_date: string
  /** Numbers of options related to the question of the poll */
  options_count: number
  /** Is the poll open/started */
  open_p: boolean
  /** Whether the poll results are displayed or not */
  display_results_p: boolean
  /** Options related to the question of the poll */
  options: Array<{
    /** ID of the poll option */
    poll_option_id: number
    /** The poll option */
    option: string
    /** Numbers of answers related to the poll option */
    answer_count: number
    /** Percentage of answers related to the poll option */
    answer_percentage: number
    /** Formatted answers percentage */
    answer_percentage_fmt: string
  }>
}

type UpdatePollDto = {
  /** ID of the poll that was updated */
  poll_id: string
}

const pollEndpoint = () => ({
  list: () => {
    return `/api/2/poll/list`
  },
  update: () => '/api/2/poll/update',
})

/**
 * Get all polls for an object (webinar)
 *
 * @param {string} objId
 */
async function getPolls(objId: number): Promise<PollDto[]> {
  const { data } = await request<ApiResponse<PollDto[]>>({
    method: HTTPMethod.GET,
    url: pollEndpoint().list(),
    data: {
      object_id: objId,
    },
  })
  return data
}

/**
 * Start a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function startPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<ApiResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: true,
    },
  })
  return data
}

/**
 * End a specific poll
 *
 * @param {number} pollId ID of the poll
 * @returns {Promise} DTO containing the ID of the updated poll
 */
async function endPoll(pollId: number): Promise<UpdatePollDto> {
  const { data } = await request<ApiResponse<UpdatePollDto>>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      open_p: false,
    },
  })
  return data
}

async function togglePollResults(pollId: number, open: boolean) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      display_results_p: open,
    },
  })
}

type CreatePollResponse = {
  poll_id: number
}

async function createPoll(
  liveId: number,
  question: string
): Promise<CreatePollResponse> {
  const { data } = await request<ApiResponse<CreatePollResponse>>({
    method: HTTPMethod.POST,
    url: '/api/2/poll/add',
    data: {
      question,
      object_id: liveId,
    },
  })
  return data
}

async function updatePollTitle(pollId: number, title: string) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: pollEndpoint().update(),
    data: {
      poll_id: pollId,
      question: title,
    },
  })
}

async function setPollOptions(pollId: number, options: string[]) {
  await request<ApiResponse>({
    method: HTTPMethod.POST,
    url: '/api/2/poll/set-options',
    data: {
      options: JSON.stringify(options),
      poll_id: pollId,
    },
  })
}

export {
  getPolls,
  startPoll,
  endPoll,
  createPoll,
  setPollOptions,
  togglePollResults,
  updatePollTitle,
}
